import { useMemo, useState } from 'react';
import { Copy, MessageCircle, MapPin, Store as StoreIcon, Users, MapPinOff, ArrowUpDown, Search, Filter, X, ChevronDown, ChevronUp, Building2, User, Phone, PhoneCall, DollarSign, Calendar, Shield } from 'lucide-react';
import { showToast } from './Toast';

const sortOptions = [
  { value: 'city-asc', label: 'City (A-Z)' },
  { value: 'visit-asc', label: 'Visit (oldest)' },
  { value: 'visit-desc', label: 'Visit (recent)' },
  { value: 'name-asc', label: 'Name (A-Z)' }
];

const viewModes = [
  { value: 'stores', label: 'Stores' },
  { value: 'sellers', label: 'Sellers Directory' },
  { value: 'seguros', label: 'Insurance Report' }
];

export function RelatorioTab({ lojas }) {
  const [viewMode, setViewMode] = useState('stores');
  const [sortBy, setSortBy] = useState('city-asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [sellerSearchTerm, setSellerSearchTerm] = useState('');
  const [seguroSearchTerm, setSeguroSearchTerm] = useState('');
  const [seguroDateFilter, setSeguroDateFilter] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [lojaFilter, setLojaFilter] = useState('all');
  const [sellerFilter, setSellerFilter] = useState('all');

  // Get all sellers with store info
  const allSellers = useMemo(() => {
    const sellers = [];
    lojas.forEach(loja => {
      (loja.sellers || []).forEach(seller => {
        sellers.push({
          id: seller.id || `${seller.name}_${seller.phone}`,
          name: seller.name || '',
          phone: seller.phone || '',
          foto: seller.foto || '',
          lojaId: loja.id,
          lojaName: loja.name,
          lojaCity: loja.city,
          lojaState: loja.state
        });
      });
    });
    return sellers.sort((a, b) => a.name.localeCompare(b.name));
  }, [lojas]);

  // Filter sellers by search
  const filteredSellers = useMemo(() => {
    if (!sellerSearchTerm.trim()) return allSellers;
    const term = sellerSearchTerm.toLowerCase();
    return allSellers.filter(s => 
      s.name.toLowerCase().includes(term) ||
      s.phone.includes(term) ||
      s.lojaName.toLowerCase().includes(term) ||
      s.lojaCity?.toLowerCase().includes(term)
    );
  }, [allSellers, sellerSearchTerm]);

  // Get unique filter options
  const filterOptions = useMemo(() => {
    const lojaNames = lojas.map(l => ({ id: l.id, name: l.name })).sort((a, b) => a.name.localeCompare(b.name));
    
    const sellers = [];
    lojas.forEach(loja => {
      (loja.sellers || []).forEach(seller => {
        if (seller.name && !sellers.find(s => s.name === seller.name && s.phone === seller.phone)) {
          sellers.push({ 
            name: seller.name, 
            phone: seller.phone || '',
            lojaId: loja.id, 
            lojaName: loja.name 
          });
        }
      });
    });
    
    return { 
      lojas: lojaNames, 
      sellers: sellers.sort((a, b) => a.name.localeCompare(b.name)) 
    };
  }, [lojas]);

  // Get all seguros (insurance records)
  const allSeguros = useMemo(() => {
    const seguros = [];
    lojas.forEach(loja => {
      (loja.sellers || []).forEach(seller => {
        (seller.seguros || []).forEach(seguro => {
          seguros.push({
            ...seguro,
            sellerName: seller.name || 'Unknown',
            sellerPhone: seller.phone || '',
            lojaName: loja.name,
            lojaId: loja.id,
            sellerId: seller.id
          });
        });
      });
    });
    return seguros.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [lojas]);

  // Filter seguros by search term and date
  const filteredSeguros = useMemo(() => {
    let result = allSeguros;
    
    // Filter by search term (seller name, client name, store name)
    if (seguroSearchTerm.trim()) {
      const term = seguroSearchTerm.toLowerCase();
      result = result.filter(s => 
        s.sellerName?.toLowerCase().includes(term) ||
        s.clientName?.toLowerCase().includes(term) ||
        s.lojaName?.toLowerCase().includes(term)
      );
    }
    
    // Filter by date range
    if (seguroDateFilter.start) {
      result = result.filter(s => s.date >= seguroDateFilter.start);
    }
    if (seguroDateFilter.end) {
      result = result.filter(s => s.date <= seguroDateFilter.end);
    }
    
    return result;
  }, [allSeguros, seguroSearchTerm, seguroDateFilter]);

  // Calculate seguros totals
  const segurosTotals = useMemo(() => {
    const totalValue = filteredSeguros.reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
    return {
      count: filteredSeguros.length,
      totalValue
    };
  }, [filteredSeguros]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (lojaFilter !== 'all') count++;
    if (sellerFilter !== 'all') count++;
    return count;
  }, [lojaFilter, sellerFilter]);

  const clearAllFilters = () => {
    setLojaFilter('all');
    setSellerFilter('all');
    setSearchTerm('');
  };

  // Filter lojas by search term and filters
  const filteredLojas = useMemo(() => {
    let result = [...lojas];
    
    // Filter by specific loja
    if (lojaFilter !== 'all') {
      result = result.filter(loja => loja.id === lojaFilter);
    }
    
    // Filter by seller
    if (sellerFilter !== 'all') {
      result = result.filter(loja => 
        (loja.sellers || []).some(s => s.name === sellerFilter)
      );
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(loja => 
        loja.name.toLowerCase().includes(term) ||
        loja.city.toLowerCase().includes(term) ||
        loja.state.toLowerCase().includes(term) ||
        (loja.manager && loja.manager.toLowerCase().includes(term)) ||
        (loja.sellers && loja.sellers.some(s => 
          s.name?.toLowerCase().includes(term) || 
          s.phone?.includes(term)
        ))
      );
    }
    
    return result;
  }, [lojas, searchTerm, lojaFilter, sellerFilter]);

  // Statistics (based on filtered lojas)
  const stats = useMemo(() => {
    const cities = new Set(filteredLojas.map(l => l.city.toLowerCase()));
    const totalSellers = filteredLojas.reduce((sum, l) => sum + (l.sellers?.length || 0), 0);
    const withoutGPS = filteredLojas.filter(l => !l.lat || !l.lng).length;

    return {
      totalLojas: filteredLojas.length,
      totalCities: cities.size,
      totalSellers,
      withoutGPS
    };
  }, [filteredLojas]);

  // Sorted and grouped lojas (based on filtered lojas)
  const { sortedLojas, groupedByCity } = useMemo(() => {
    let sorted = [...filteredLojas];

    switch (sortBy) {
      case 'city-asc':
        sorted.sort((a, b) => a.city.localeCompare(b.city));
        break;
      case 'visit-asc':
        sorted.sort((a, b) => (a.visit || '').localeCompare(b.visit || ''));
        break;
      case 'visit-desc':
        sorted.sort((a, b) => (b.visit || '').localeCompare(a.visit || ''));
        break;
      case 'name-asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    // Group by city for city sort
    const grouped = {};
    if (sortBy === 'city-asc') {
      sorted.forEach(loja => {
        const city = loja.city.toUpperCase();
        if (!grouped[city]) grouped[city] = [];
        grouped[city].push(loja);
      });
    }

    return { sortedLojas: sorted, groupedByCity: grouped };
  }, [filteredLojas, sortBy]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  };

  // Generate export text (without dates) - uses filtered lojas
  const generateExportText = () => {
    const lines = [];
    const cities = {};

    // Group by city
    filteredLojas.forEach(loja => {
      const city = loja.city.toUpperCase();
      if (!cities[city]) cities[city] = [];
      cities[city].push(loja);
    });

    // Sort cities alphabetically
    const sortedCities = Object.keys(cities).sort();

    sortedCities.forEach((city, idx) => {
      lines.push('===================================');
      lines.push(`  ${city}`);
      lines.push('===================================');

      cities[city].forEach(loja => {
        lines.push(`  ${loja.name}`);
        lines.push(`  Address: ${loja.address}, ${loja.city}, ${loja.state} ${loja.zip}`);
        if (loja.manager) {
          lines.push(`  Manager: ${loja.manager}`);
        }
        if (loja.sellers && loja.sellers.length > 0) {
          lines.push('  Salespeople:');
          loja.sellers.forEach(s => {
            lines.push(`    -- ${s.name}  ${s.phone}`);
          });
        }
        if (loja.notes) {
          lines.push(`  Notes: ${loja.notes}`);
        }
        lines.push('');
      });

      if (idx < sortedCities.length - 1) {
        lines.push('-----------------------------------');
        lines.push('');
      }
    });

    return lines.join('\n');
  };

  const handleCopy = async () => {
    const text = generateExportText();
    try {
      await navigator.clipboard.writeText(text);
      showToast('Report copied!');
    } catch (err) {
      showToast('Error copying', 'error');
    }
  };

  const handleWhatsApp = async () => {
    const text = generateExportText();
    
    // Try Web Share API first (works on mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Dealer Manager - Report',
          text: text
        });
        showToast('Shared successfully!');
        return;
      } catch (err) {
        // User cancelled or share failed, fall back to clipboard
        if (err.name !== 'AbortError') {
          console.log('Share failed, using clipboard fallback');
        }
      }
    }
    
    // Fallback: try WhatsApp URL scheme
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      window.open(whatsappUrl, '_blank');
      showToast('Opening WhatsApp...');
    } else {
      // Desktop fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(text);
        showToast('Text copied! Paste in WhatsApp');
      } catch (err) {
        showToast('Error sharing', 'error');
      }
    }
  };

  const showVisitBadge = sortBy === 'visit-asc' || sortBy === 'visit-desc';

  // Copy sellers list
  const handleCopySellers = async () => {
    const text = filteredSellers.map(s => 
      `${s.name} | ${s.phone || 'No phone'} | ${s.lojaName}`
    ).join('\n');
    
    const header = `SELLERS DIRECTORY (${filteredSellers.length})\n${'='.repeat(40)}\n\n`;
    
    try {
      await navigator.clipboard.writeText(header + text);
      showToast('Sellers list copied!');
    } catch (err) {
      showToast('Error copying', 'error');
    }
  };

  return (
    <div className="flex flex-col gap-4 pb-20">
      {/* View Mode Tabs */}
      <div className="flex gap-2 p-1 bg-[var(--bg-chip)] rounded-lg">
        {viewModes.map(mode => (
          <button
            key={mode.value}
            onClick={() => setViewMode(mode.value)}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              viewMode === mode.value
                ? 'bg-[var(--accent-primary)] text-white'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            {mode.value === 'sellers' && <Users size={14} className="inline mr-1.5" />}
            {mode.value === 'stores' && <StoreIcon size={14} className="inline mr-1.5" />}
            {mode.label}
          </button>
        ))}
      </div>

      {/* SELLERS DIRECTORY VIEW */}
      {viewMode === 'sellers' && (
        <>
          {/* Sellers Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
            <input
              data-testid="input-search-sellers"
              type="text"
              placeholder="Search seller by name, phone or store..."
              value={sellerSearchTerm}
              onChange={(e) => setSellerSearchTerm(e.target.value)}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg pl-10 pr-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
            />
          </div>

          {/* Sellers Stats */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-[var(--text-secondary)]">
              <span className="font-bold text-[var(--text-primary)]">{filteredSellers.length}</span> seller{filteredSellers.length !== 1 ? 's' : ''} found
            </p>
            <button
              onClick={handleCopySellers}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[var(--accent-primary)] text-white rounded-lg hover:opacity-90"
            >
              <Copy size={12} /> Copy List
            </button>
          </div>

          {/* Sellers List */}
          <div className="flex flex-col gap-2">
            {filteredSellers.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)]">
                {sellerSearchTerm ? 'No sellers found' : 'No sellers registered'}
              </div>
            ) : (
              filteredSellers.map((seller, idx) => (
                <div
                  key={`${seller.id}-${idx}`}
                  className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-4 flex items-center gap-4"
                >
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-[var(--accent-primary)]/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {seller.foto ? (
                      <img src={seller.foto} alt={seller.name} className="w-full h-full object-cover" />
                    ) : (
                      <User size={24} className="text-[var(--accent-primary)]" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-[var(--text-primary)] truncate">{seller.name}</h4>
                    <div className="flex items-center gap-1 text-sm text-[var(--text-secondary)]">
                      <StoreIcon size={12} />
                      <span className="truncate">{seller.lojaName}</span>
                      {seller.lojaCity && (
                        <span className="text-[var(--text-muted)]">• {seller.lojaCity}</span>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  {seller.phone ? (
                    <a
                      href={`tel:${seller.phone.replace(/\D/g, '')}`}
                      className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                    >
                      <PhoneCall size={16} />
                      <span className="hidden sm:inline">{seller.phone}</span>
                    </a>
                  ) : (
                    <span className="px-3 py-2 bg-[var(--bg-chip)] text-[var(--text-muted)] rounded-lg text-xs">
                      No phone
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* INSURANCE REPORT VIEW */}
      {viewMode === 'seguros' && (
        <>
          {/* Seguros Search and Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
              <input
                data-testid="input-search-seguros"
                type="text"
                placeholder="Search by seller, client or store..."
                value={seguroSearchTerm}
                onChange={(e) => setSeguroSearchTerm(e.target.value)}
                className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg pl-10 pr-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] placeholder:text-[var(--text-muted)]"
              />
            </div>
            
            {/* Date Filters */}
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs text-[var(--text-muted)] mb-1">From</label>
                <input
                  type="date"
                  value={seguroDateFilter.start}
                  onChange={(e) => setSeguroDateFilter({ ...seguroDateFilter, start: e.target.value })}
                  className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-[var(--text-muted)] mb-1">To</label>
                <input
                  type="date"
                  value={seguroDateFilter.end}
                  onChange={(e) => setSeguroDateFilter({ ...seguroDateFilter, end: e.target.value })}
                  className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)]"
                />
              </div>
              {(seguroDateFilter.start || seguroDateFilter.end) && (
                <button
                  onClick={() => setSeguroDateFilter({ start: '', end: '' })}
                  className="self-end px-3 py-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Seguros Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[var(--bg-card)] p-4 rounded-xl border border-[var(--border-color)] flex flex-col items-center">
              <Shield size={24} className="text-green-500 mb-2" />
              <span className="text-2xl font-bold text-[var(--text-primary)]">{segurosTotals.count}</span>
              <span className="text-xs text-[var(--text-secondary)]">Total Insurance</span>
            </div>
            <div className="bg-[var(--bg-card)] p-4 rounded-xl border border-[var(--border-color)] flex flex-col items-center">
              <DollarSign size={24} className="text-green-500 mb-2" />
              <span className="text-2xl font-bold text-green-500">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(segurosTotals.totalValue)}
              </span>
              <span className="text-xs text-[var(--text-secondary)]">Total Value</span>
            </div>
          </div>

          {/* Seguros List */}
          <div className="flex flex-col gap-2">
            {filteredSeguros.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)]">
                <Shield size={48} className="mx-auto mb-3 opacity-30" />
                <p>{allSeguros.length === 0 ? 'No insurance records' : 'No records found'}</p>
              </div>
            ) : (
              filteredSeguros.map((seguro, idx) => (
                <div
                  key={`${seguro.id}-${idx}`}
                  className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <DollarSign size={20} className="text-green-500" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-green-500">
                          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(seguro.value)}
                        </div>
                        <div className="text-xs text-[var(--text-muted)] flex items-center gap-1">
                          <Calendar size={10} />
                          {formatDate(seguro.date)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {seguro.clientName && (
                    <div className="flex items-center gap-2 mb-2 text-sm text-[var(--accent-primary)]">
                      <User size={14} />
                      <span className="font-medium">Client: {seguro.clientName}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 text-sm text-[var(--text-secondary)]">
                    <div className="flex items-center gap-1">
                      <User size={12} />
                      <span>{seguro.sellerName}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <StoreIcon size={12} />
                      <span>{seguro.lojaName}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* STORES VIEW */}
      {viewMode === 'stores' && (
        <>
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
            <input
              data-testid="input-search-relatorio"
              type="text"
              placeholder="Search by name, city, manager, seller or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg pl-10 pr-4 py-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)] focus:ring-1 focus:ring-[var(--border-focus)] transition-all placeholder:text-[var(--text-muted)]"
            />
          </div>

      {/* Filter Toggle Button */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeFiltersCount > 0 
              ? 'bg-[var(--accent-primary)] text-white' 
              : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
          }`}
          data-testid="btn-toggle-filters-report"
        >
          <Filter size={16} />
          Filters
          {activeFiltersCount > 0 && (
            <span className="bg-white text-[var(--accent-primary)] text-xs font-bold px-1.5 py-0.5 rounded-full">
              {activeFiltersCount}
            </span>
          )}
          {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        
        {/* Clear Filters */}
        {activeFiltersCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1 px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <X size={14} />
            Clear
          </button>
        )}
        
        {/* Sort */}
        <select
          data-testid="select-sort-relatorio"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="ml-auto bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--border-focus)]"
        >
          {sortOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Expandable Filters Panel */}
      {showFilters && (
        <div className="p-4 bg-[var(--bg-card)] rounded-xl border border-[var(--border-color)] space-y-4">
          {/* Store Filter */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-[var(--text-secondary)] mb-2">
              <StoreIcon size={14} />
              STORE
            </label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              <button
                onClick={() => setLojaFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  lojaFilter === 'all'
                    ? 'bg-[var(--accent-primary)] text-white'
                    : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
                }`}
              >
                All Stores
              </button>
              {filterOptions.lojas.map(loja => (
                <button
                  key={loja.id}
                  onClick={() => setLojaFilter(loja.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    lojaFilter === loja.id
                      ? 'bg-[var(--accent-primary)] text-white'
                      : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
                  }`}
                >
                  {loja.name}
                </button>
              ))}
            </div>
          </div>

          {/* Seller Filter */}
          {filterOptions.sellers.length > 0 && (
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-[var(--text-secondary)] mb-2">
                <User size={14} />
                SELLER
              </label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                <button
                  onClick={() => setSellerFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    sellerFilter === 'all'
                      ? 'bg-green-600 text-white'
                      : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
                  }`}
                >
                  All Sellers
                </button>
                {filterOptions.sellers.map((seller, idx) => (
                  <button
                    key={`${seller.name}-${seller.phone}-${idx}`}
                    onClick={() => setSellerFilter(seller.name)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      sellerFilter === seller.name
                        ? 'bg-green-600 text-white'
                        : 'bg-[var(--bg-chip)] text-[var(--text-secondary)] hover:bg-[var(--bg-chip-hover)]'
                    }`}
                    title={`${seller.lojaName}${seller.phone ? ` - ${seller.phone}` : ''}`}
                  >
                    {seller.name}
                    {seller.phone && <span className="ml-1 opacity-70">({seller.phone})</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active Filters Pills */}
      {activeFiltersCount > 0 && !showFilters && (
        <div className="flex flex-wrap gap-2">
          {lojaFilter !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--accent-primary)]/20 text-[var(--accent-primary)] rounded-full text-xs font-medium">
              <StoreIcon size={12} />
              {filterOptions.lojas.find(l => l.id === lojaFilter)?.name || 'Store'}
              <button onClick={() => setLojaFilter('all')} className="hover:text-red-500">
                <X size={12} />
              </button>
            </span>
          )}
          {sellerFilter !== 'all' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-600 rounded-full text-xs font-medium">
              <User size={12} />
              {sellerFilter}
              <button onClick={() => setSellerFilter('all')} className="hover:text-red-500">
                <X size={12} />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          icon={StoreIcon}
          value={stats.totalLojas}
          label="Total Stores"
          testId="stat-total-lojas"
        />
        <StatCard
          icon={MapPin}
          value={stats.totalCities}
          label="Total Cities"
          testId="stat-total-cidades"
        />
        <StatCard
          icon={Users}
          value={stats.totalSellers}
          label="Total Sellers"
          testId="stat-total-vendedores"
        />
        <StatCard
          icon={MapPinOff}
          value={stats.withoutGPS}
          label="Stores without GPS"
          testId="stat-sem-gps"
        />
      </div>

      {/* Export Buttons */}
      <div className="flex gap-2">
        <button
          data-testid="btn-copy-relatorio"
          onClick={handleCopy}
          className="flex-1 h-10 bg-[var(--accent-primary)] text-white rounded-lg flex items-center justify-center gap-2 active:opacity-90 font-medium text-sm"
        >
          <Copy size={16} /> Copy
        </button>
        <button
          data-testid="btn-export-whatsapp"
          onClick={handleWhatsApp}
          className="flex-1 h-10 bg-green-600 text-white rounded-lg flex items-center justify-center gap-2 active:bg-green-700 font-medium text-sm"
        >
          <MessageCircle size={16} /> WhatsApp
        </button>
      </div>

      {/* Report Content */}
      {sortBy === 'city-asc' ? (
        // Grouped by city view
        Object.entries(groupedByCity).map(([city, cityLojas]) => (
          <div key={city} className="bg-[var(--bg-card)] rounded-xl overflow-hidden border border-[var(--border-color)] shadow-sm">
            {/* City Header */}
            <div className="bg-[var(--bg-header)] px-4 py-3 flex items-center gap-2">
              <MapPin size={16} className="text-[var(--accent-sky)]" />
              <span className="text-[var(--accent-sky)] font-bold uppercase tracking-wide text-sm">
                {city}
              </span>
              <span className="ml-auto text-xs text-[var(--text-muted)]">
                {cityLojas.length} store{cityLojas.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Lojas in city */}
            <div className="p-3 flex flex-col gap-3">
              {cityLojas.map(loja => (
                <LojaReportCard key={loja.id} loja={loja} showVisitBadge={false} formatDate={formatDate} />
              ))}
            </div>
          </div>
        ))
      ) : (
        // Flat list view
        <div className="flex flex-col gap-3">
          {sortedLojas.map(loja => (
            <LojaReportCard key={loja.id} loja={loja} showVisitBadge={showVisitBadge} formatDate={formatDate} />
          ))}
        </div>
      )}

      {filteredLojas.length === 0 && (
        <div className="text-center py-12 text-[var(--text-muted)]">
          <StoreEmptyIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{lojas.length === 0 ? 'No stores registered' : 'No stores found'}</p>
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="mt-2 text-sm text-[var(--accent-primary)] hover:underline"
            >
              Clear search
            </button>
          )}
        </div>
      )}
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, value, label, testId }) {
  return (
    <div 
      data-testid={testId}
      className="bg-[var(--bg-card)] p-4 rounded-xl border border-[var(--border-color)] flex flex-col items-center text-center shadow-sm"
    >
      <Icon size={24} className="text-[var(--accent-primary)] mb-2" />
      <span className="text-2xl font-bold text-[var(--text-primary)]">{value}</span>
      <span className="text-xs text-[var(--text-secondary)] font-medium">{label}</span>
    </div>
  );
}

function LojaReportCard({ loja, showVisitBadge, formatDate }) {
  return (
    <div 
      data-testid={`relatorio-loja-${loja.id}`}
      className="bg-[var(--bg-card)] rounded-[12px] border border-[var(--border-color)] border-l-[4px] border-l-[var(--accent-primary)] p-4"
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-base font-semibold text-[var(--text-primary)]">{loja.name}</h4>
        {showVisitBadge && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[var(--accent-primary)]/20 text-[var(--accent-primary)]">
            {formatDate(loja.visit)}
          </span>
        )}
      </div>
      
      <p className="text-sm text-[var(--text-secondary)] mb-2">
        {loja.address}, {loja.city}, {loja.state} {loja.zip}
      </p>

      {loja.manager && (
        <p className="text-sm text-[var(--text-secondary)] mb-2">
          <span className="font-medium">Manager:</span> {loja.manager}
        </p>
      )}

      {loja.sellers && loja.sellers.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {loja.sellers.map((seller) => (
            <span
              key={seller.id || `${seller.name}_${seller.phone}`}
              className="text-xs bg-[var(--bg-chip)] text-[var(--text-secondary)] px-2 py-1 rounded-full"
            >
              {seller.name}
            </span>
          ))}
        </div>
      )}

      {loja.notes && (
        <div className="text-xs text-[var(--text-muted)] border-l-2 border-[var(--accent-primary)] pl-2 mt-2">
          {loja.notes}
        </div>
      )}
    </div>
  );
}

function StoreEmptyIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 21h18M3 7l9-4 9 4M6 7v14M18 7v14M9 21v-6h6v6M9 10h.01M15 10h.01" />
    </svg>
  );
}
